import { Reactions } from '@lib/components/Reactions'
import { useClickOutside } from '@lib/hooks'
import { IconMore, IconReaction } from '@lib/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useAuth } from '@/hooks'
import { useProjectAccess } from '../../../../../../../hooks'
import css from './Actions.module.scss'
import { Settings } from './components/Settings'

type Props = {
  onClickDelete?: () => void
  onClickEdit?: () => void
  onSelectEmoji?: (emoji: string) => void
  owner: string
}

const Actions: React.FC<Props> = ({
  onClickDelete,
  onClickEdit,
  onSelectEmoji,
  owner,
}) => {
  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)

  const [showSettings, setShowSettings] = useState(false)
  const [showReactions, setShowReactions] = useState(false)
  const [openUpwards, setOpenUpwards] = useState<null | boolean>(null)

  const reactionsRef = useRef<HTMLDivElement>(null)

  const auth = useAuth()

  const { hasAccess } = useProjectAccess('DELETE_OTHER_COMMENT')
  const { hasAccess: hasAccessToEmojiReactions } =
    useProjectAccess('EMOJI_REACTIONS')

  const clickOutsideSettings = useClickOutside<HTMLDivElement>(
    showSettings,
    () => setShowSettings(false)
  )
  const clickOutsideReactions = useClickOutside<HTMLDivElement>(
    showReactions,
    () => setShowReactions(false)
  )

  const handleDelete = () => {
    setShowSettings(false)

    onClickDelete?.()
  }

  const handleEdit = () => {
    setShowSettings(false)

    onClickEdit?.()
  }

  const handleSelectEmoji = (emoji: string) => {
    setShowReactions(false)

    onSelectEmoji?.(emoji)
  }

  useEffect(() => {
    if (showReactions) {
      const windowHeight = window.innerHeight
      const reactionsRect = reactionsRef.current?.getBoundingClientRect()
      const reactionsTop = reactionsRect?.top ?? 0
      const reactionsHeight = reactionsRect?.height ?? 0

      const reactionsPos = reactionsTop + reactionsHeight

      if (reactionsPos > windowHeight) {
        return setOpenUpwards(true)
      }

      return setOpenUpwards(false)
    }

    setOpenUpwards(null)
  }, [showReactions])

  const opacity = openUpwards === null ? 0 : 1
  const top = !openUpwards ? 'calc(100% + 8px)' : 'unset'
  const bottom = openUpwards ? 'calc(100% + 8px)' : 'unset'

  // @TODO temporarily hide reactions
  const hideReactions = true

  if (!auth.authenticated) {
    return null
  }

  return (
    <div className={css.container}>
      {!hideReactions && hasAccessToEmojiReactions && canEdit && (
        <div className={css.item} {...clickOutsideReactions}>
          <div
            className={css.icon}
            onClick={() => setShowReactions(!showReactions)}
          >
            <IconReaction />
          </div>

          {showReactions && (
            <div
              className={css.reactions}
              style={{ opacity, top, bottom }}
              ref={reactionsRef}
            >
              <Reactions onSelect={handleSelectEmoji} />
            </div>
          )}
        </div>
      )}

      {(hasAccess || auth.id === owner) && (
        <div className={css.item} {...clickOutsideSettings}>
          <div
            className={css.icon}
            onClick={() => setShowSettings(!showSettings)}
          >
            <IconMore />
          </div>

          {showSettings && (
            <div className={css.settings}>
              <Settings
                onClickDelete={handleDelete}
                onClickEdit={handleEdit}
                owner={owner}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Actions
